import { useMemo } from 'react';
import dayjs from 'dayjs';
import { BLOCKCHAIN_CLOSED_BETS_BEFORE_ROUND_END_TIME_MS } from '../utils/blockchain-consts.ts';
import { ENV_CONSTS } from '../utils/env-consts.ts';
import { useGameRoundSliderDurationRenderData } from './useGameRoundSliderDurationRenderData.ts';
import { useIsBlockchainVersion } from './useIsBlockchainVersion.ts';

const TIME_RANGES = (() => {
  const scheduledTimes = ENV_CONSTS.B2C_LIVE_SCHEDULED_GAMES_UTC_TIMES;
  if (!scheduledTimes) return [];

  return scheduledTimes
    .split(';')
    .map((range) => {
      const [startTime, endTime] = range.split('-');
      if (!startTime || !endTime) return null;

      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);

      return {
        start: startHour * 60 + startMinute,
        end: endHour * 60 + endMinute,
      };
    })
    .filter((range): range is NonNullable<typeof range> => range !== null);
})();

const CLOSING_THRESHOLD_MINUTES = 2;

export const useIsBlockchainPredictionClosed = () => {
  const blockchainVersion = useIsBlockchainVersion();
  const { durationInSeconds, timeDifference } =
    useGameRoundSliderDurationRenderData();

  return useMemo(() => {
    if (!blockchainVersion) return false;

    const leftTime = durationInSeconds - timeDifference;
    const restrictedTime =
      BLOCKCHAIN_CLOSED_BETS_BEFORE_ROUND_END_TIME_MS / 1000;

    if (leftTime <= restrictedTime) return true;

    if (TIME_RANGES.length === 0) return false;

    const currentTimeInMinutes = (() => {
      const now = dayjs().utc();
      return now.hour() * 60 + now.minute();
    })();

    for (const range of TIME_RANGES) {
      const isRangeActive =
        currentTimeInMinutes >= range.start &&
        currentTimeInMinutes <= range.end;
      if (isRangeActive) {
        const minutesUntilEnd = range.end - currentTimeInMinutes;
        if (minutesUntilEnd <= CLOSING_THRESHOLD_MINUTES) {
          return true;
        }
      }
    }

    return false;
  }, [blockchainVersion, durationInSeconds, timeDifference]);
};
